import { gofarService } from './gofar-service';
import { HTTP_METHODS, HTTP_STATUS_CODES } from './config';
import { CHARGEBEE_ADD_ONS, CHARGEBEE_SUBSCRIPTION_PLANS, MAX_ALLOWED_QUANTITY, PRODUCT_IMAGES } from './constants';
import { STATES } from './data/address-data-provider';
import * as helper from './helper';

export const mapToSubscriptionProducts = (selectedProducts, countrySetting, coupon, quantity, selectedPlan)=> {
  let isDiscountAppliedAlready = false;
  const isApplicableToManyProducts = coupon.meta_data?.applicableToMany || false;
  const subscriptionProducts = selectedProducts.map((product) => {
    const chargebeeProduct = CHARGEBEE_ADD_ONS.find((chargebeeProduct) => chargebeeProduct.id === product);
    const originalPrice = chargebeeProduct.price;
    const priceInSelectedCurrency = helper.convertToCurrency(originalPrice, countrySetting) / 100;
    const isDiscountApplicableForProduct = helper.checkIfDiscountIsApplicableForProduct(selectedPlan, product, coupon, isDiscountAppliedAlready);
    // only one add_ons will be selected while creating coupon making sure that this process doesn't apply same coupon on both adapter and ray
    const discountedPrice = isDiscountApplicableForProduct || isApplicableToManyProducts ? helper.calculatePriceWithDiscount(originalPrice, coupon, countrySetting) : originalPrice;
    isDiscountAppliedAlready = discountedPrice < originalPrice;
    const discountedPriceInSelectedCurrency = helper.convertToCurrency(discountedPrice, countrySetting) / 100;
    return {
      id: chargebeeProduct.id,
      name: chargebeeProduct.name,
      image: chargebeeProduct.imageUrl,
      quantity,
      originalPrice,
      price: discountedPrice,
      priceInSelectedCurrency,
      discountedPriceInSelectedCurrency
    };
  });
  return subscriptionProducts;
}

export const mapToSubscriptionPlan = (selectedPlan, countrySetting, coupon, quantity, isDiscountAppliedOnProducts)=> {
  const chargebeePlan = CHARGEBEE_SUBSCRIPTION_PLANS.find((plan) => plan.id === selectedPlan);
  // original price will always have a round figure in hundreds as they are set in AUD
  const originalPrice = chargebeePlan.price;
  // convert them in round figure of 100th to have better pricing
  const priceInSelectedCurrency = helper.convertToCurrency(originalPrice, countrySetting) / 100;

  const discountedPrice =
    !isDiscountAppliedOnProducts && coupon.apply_on === 'each_specified_item' && coupon.plan_ids?.includes(chargebeePlan.id)
      ? helper.calculatePriceWithDiscount(originalPrice, coupon, countrySetting)
      : originalPrice;
  const discountedPriceInSelectedCurrency = helper.convertToCurrency(discountedPrice, countrySetting) / 100;
  const subscriptionPlan = {
    id: chargebeePlan.id,
    name: chargebeePlan.name,
    image: PRODUCT_IMAGES.SUBSCRIPTION,
    quantity,
    originalPrice,
    price: discountedPrice,
    priceInSelectedCurrency,
    discountedPriceInSelectedCurrency
  };
  return subscriptionPlan;

}

export const getDefaultShipmentOption = (countrySetting)=> {
  const shippingOptionForCountry = countrySetting.shippingOptions;
  const defaultShippingOption = shippingOptionForCountry[0];
  return {
    ...defaultShippingOption,
    priceInSelectedCurrency: helper.convertToCurrency(defaultShippingOption.price, countrySetting) / 100
  };
}


export const mapToSelectedShippingOption = (countrySetting, selectedShippingId)=> {
  const shippingOptionForCountry = countrySetting.shippingOptions;
  const shippingOption = shippingOptionForCountry.find(
    (shippingOption) =>  shippingOption.id === selectedShippingId
  );
  const chargebeeShippingOption = shippingOption || shippingOptionForCountry[0];
  const originalShippingPrice = chargebeeShippingOption.price;
  // discount will not be applicable in shipping
  const selectedShippingOption = {
    id: chargebeeShippingOption.id,
    name: chargebeeShippingOption.name,
    price: originalShippingPrice,
    priceInSelectedCurrency: helper.convertToCurrency(originalShippingPrice, countrySetting) / 100
  };
  return selectedShippingOption;
}

export const mapToSubscriptionCheckout = (
  selectedPlan,
  products,
  coupon,
  countrySetting,
  selectedQuantity,
  selectedShippingOption
) => {
  // TODO: should be able to have different quantities for Subscription, Ray and Adapter in future
  const quantity =
    parseInt(selectedQuantity, 10) > MAX_ALLOWED_QUANTITY ? MAX_ALLOWED_QUANTITY : parseInt(selectedQuantity, 10);
  const productsTotalAmount = products.reduce(
    (total, product) => total + product.originalPrice * product.quantity,
    0
  );
  const productsTotalDiscountedAmount = products.reduce((total, product)=> total + product.price * product.quantity, 0);
  const productsTotalAmountInSelectedCurrency = products.reduce(
    (total, product) => total + (product.priceInSelectedCurrency * product.quantity),
    0
  );

  const isDiscountAppliedOnProducts = productsTotalDiscountedAmount < productsTotalAmount;

  const plan = mapToSubscriptionPlan(selectedPlan, countrySetting, coupon, quantity, isDiscountAppliedOnProducts);
  const isDiscountAppliedOnPlan =  plan.price < plan.originalPrice;

  const planTotalAmount = plan.originalPrice * plan.quantity;
  const planTotalAmountInSelectedCurrency = plan.priceInSelectedCurrency * plan.quantity;

  const originalSubTotal = planTotalAmount + productsTotalAmount;
  const originalSubTotalInSelectedCurrency = planTotalAmountInSelectedCurrency +  productsTotalAmountInSelectedCurrency;

  const totalDiscountedAmount = helper.getTotalDiscountedAmount(plan, products, isDiscountAppliedOnPlan);
  const totalDiscountedAmountInSelectedCurrency = helper.convertToCurrency(totalDiscountedAmount, countrySetting) / 100; 


  const originalSubTotalWithDiscount = (originalSubTotal - totalDiscountedAmount) / 100;
  const originalSubTotalWithDiscountInSelectedCurrency = originalSubTotalInSelectedCurrency - totalDiscountedAmountInSelectedCurrency; 


  const discountedSubTotal =
    coupon.apply_on === 'invoice_amount'
      ? helper.calculatePriceWithDiscount(originalSubTotal, coupon, countrySetting)
      : originalSubTotal;
  const discountedSubTotalInSelectedCurrency = helper.convertToCurrency(discountedSubTotal, countrySetting) / 100;

  const grandTotal = coupon.apply_on === 'invoice_amount' ? discountedSubTotal + selectedShippingOption.price : originalSubTotalWithDiscount * 100 + selectedShippingOption.price;

  const grandTotalInSelectedCurrency = coupon.apply_on === 'invoice_amount'? discountedSubTotalInSelectedCurrency + selectedShippingOption.priceInSelectedCurrency: originalSubTotalWithDiscountInSelectedCurrency + selectedShippingOption.priceInSelectedCurrency;

  const message = discountedSubTotal < originalSubTotal ? `Coupon ${coupon.code} has been applied.`: '';
  
  const updatedCoupon = {
    ...coupon,
    message
  }

  const result = {
    plan,
    quantity,
    originalSubTotal,
    originalSubTotalInSelectedCurrency,
    originalSubTotalWithDiscount,
    originalSubTotalWithDiscountInSelectedCurrency,
    subTotal: discountedSubTotal,
    subTotalInSelectedCurrency: discountedSubTotalInSelectedCurrency,
    grandTotal,
    grandTotalInSelectedCurrency,
    coupon: updatedCoupon
  };
  return result;
};

export const validateCouponCode = async (couponCode, planName, addOns) => {
  if (!couponCode) {
    return {
      isValid: false
    };
  }
  if(planName.includes('basic')){
    return {
      isValid: false,
      message: 'Coupon is not available for Basic Plan.'
    };
  }
  if(couponCode.toLowerCase().includes('grandfathered')){
    return {
      isValid: false,
      message: 'Invalid Coupon Code'
    };
  }

  const coupon_result = await gofarService(`/api/checkout/V2/validate-couponcode`, HTTP_METHODS.POST, {
    couponCode: couponCode,
    planName,
    addOns
  });
  return {
    ...coupon_result.data,
    isValid: !coupon_result.data.message,
  };
};

const getCouponIds = (coupon, quantity) => {
    const { code, parentCode, apply_on, discount_type } = coupon;
    if (!code || !parentCode) {
      return [];
    }

    /**
     * If single quantity is ordered, one coupon is used
     * If coupon is to apply on invoice amount which is total, one coupon will be used
     * If discount type is percentage, one coupon will be used otherwise discount will be used multiple times on total amount of addons
     */
    if (quantity === 1 || apply_on === 'invoice_amount' || discount_type === 'percentage') {
      return [code];
    }
    /**
     * We can't use same coupon multiple times in a subscription, so we have manually generated 8 other clones of same coupon
     * and will be picked here based on number of quantities
     */
    const codes = [parentCode];
    for (let i = 2; i <= quantity; i += 1) {
      codes.push(`${parentCode}_${i}`);
    }
    return codes;
}

export const mapToChargebeeSubscriptionRequestPayload = async (customer, subscription, products, coupon, shipment, paymentData, referral, paypalExpress = {}) => {
  const subscriptionProducts = products.map((p) => {
    return { id: p.id, quantity: subscription.quantity };
  });
  // shipment has been created as a add-on in chargebee to include in the payment
  const shipmentAddOn = {id: shipment.id, quantity: 1 };
  const address = {
    first_name: customer.firstName,
      last_name: customer.lastName,
      company: customer.companyName,
      line1: customer.address,
      line2: customer.apartment,
      line3: customer.suburb,
      state: customer.state,
      city: customer.city,
      zip: customer.postalCode,
      country: customer.country
  }
  const subscriptionPayload = {
    ...paypalExpress,
    plan_id: subscription.plan.id,
    auto_collection: 'on',
    payment_intent: paymentData,
    plan_quantity: subscription.quantity,
    coupon_ids: [coupon.code],
    // Will be used later to get list of codes
    parentCode: coupon.parentCode,
    parentCodeApplyOn: coupon.apply_on,
    discountType: coupon.discount_type,
    cf_applied_referral_code: referral.code,
    cf_applied_referral_code_type: referral.type,
    // Chargebee Order reporter scheduler later uses this info to list all subscription made via website
    meta_data: {
      order_source: 'chargebee'
    },
    billing_address: {
      ...address,
      state_code: customer.stateCode,
    },
    // as part of phase 1, we will use same address as both billing and shipping
    // todo: we will later allow user to set different addresses for them
    // do not send statecode to chargebee since it's enabled for selected countries only in chargebee
    // https://apidocs.chargebee.com/docs/api/addresses#address_state_code
    shipping_address: address,
    customer: {
      first_name: customer.firstName,
      last_name: customer.lastName,
      email: customer.email,
      locale: 'en-US',
      company: customer.company,
      phone: customer.phone
    },
    addons: [...subscriptionProducts, shipmentAddOn]
  };
  return subscriptionPayload;
};

export const mapToChargebeeSubscriptionRequestPayloadV2 = async (customer, subscription, products, coupon, shipment, paymentData, referral, paypalExpress = {}) => {
  const subscriptionProducts = products.map((p) => {
    return { item_price_id: p.id, quantity: subscription.quantity };
  });
  // shipment has been created as a add-on in chargebee to include in the payment
  const shipmentAddOn = {item_price_id: shipment.id, quantity: 1 };
  const coupon_ids = getCouponIds(coupon, subscription.quantity);
  const address = {
    first_name: customer.firstName,
    last_name: customer.lastName,
    email: customer.email,
    company: customer.companyName,
    phone: customer.phone,
    line1: customer.address,
    line2: customer.apartment,
    line3: customer.suburb,
    city: customer.city,
    state: customer.state,
    country: customer.country,
    zip: customer.postalCode,
  }
  const subscriptionPayload = {
    // ...paypalExpress,
    plan_id: subscription.plan.id,
    auto_collection: 'on',
    payment_intent: paymentData,
    plan_quantity: subscription.quantity,
    coupon_ids,
    // Chargebee Order reporter scheduler later uses this info to list all subscription made via website
    cf_applied_referral_code: referral.code,
    cf_applied_referral_code_type: referral.type,
    billingAgreementId: paypalExpress.payment_method && paypalExpress.payment_method?.reference_id.length > 0 ? paypalExpress.payment_method.reference_id : '',
    meta_data: {
      order_source: 'chargebee'
    },
    billing_address: {
      ...address,
      state_code: customer.stateCode,
    },
    // as part of phase 1, we will use same address as both billing and shipping
    // todo: we will later allow user to set different addresses for them
    // do not send statecode to chargebee since it's enabled in chargebee for selected countries only
    // https://apidocs.chargebee.com/docs/api/addresses#address_state_code
    shipping_address: address,
    customer: {
      first_name: customer.firstName,
      last_name: customer.lastName,
      email: customer.email,
      locale: 'en-US',
      company: customer.company,
      phone: customer.phone
    },
    subscription_items: [...subscriptionProducts, shipmentAddOn, {
      item_price_id: subscription.plan.id,
      quantity: subscription.quantity
    }]
  };
  return subscriptionPayload;
};

export const fetchPaypalCustomerDetail = async (token, customer) => {
  const paypal_customer_result = await gofarService(
    `/api/checkout/paypal-customer-data?token=${token}`,
    HTTP_METHODS.GET
  );
  if (paypal_customer_result.status !== HTTP_STATUS_CODES.SUCCESS) {
    return customer;
  }
  const paypalCustomer = paypal_customer_result.data;
  const stateName = STATES[paypalCustomer.countryCode][paypalCustomer.state];
  const gofarCustomer = {
    email: paypalCustomer.email.length > 0 ? decodeURIComponent(paypalCustomer.email) : customer.email,
    firstName: paypalCustomer.firstName.length > 0 ? decodeURIComponent(paypalCustomer.firstName) : customer.firstName,
    lastName: paypalCustomer.lastName.length > 0 ? decodeURIComponent(paypalCustomer.lastName) : customer.lastName,
    address: paypalCustomer.address_line1.length > 0 ? decodeURIComponent(paypalCustomer.address_line1) : customer.address,
    apartment: paypalCustomer.address_line2.length > 0 ? decodeURIComponent(paypalCustomer.address_line2) : customer.apartment,
    city: paypalCustomer.city.length > 0 ? decodeURIComponent(paypalCustomer.city) : customer.city,
    suburb: paypalCustomer.city.length > 0 ? decodeURIComponent(paypalCustomer.city) : customer.suburb,
    state: stateName && stateName.length > 0 ? stateName : customer.state,
    stateCode: paypalCustomer.state.length>0 ? paypalCustomer.state : customer.stateCode,
    postalCode: paypalCustomer.zip && paypalCustomer.zip.length > 0 ? paypalCustomer.zip : customer.postalCode,
    country: paypalCustomer.countryCode.length > 0 ? paypalCustomer.countryCode : customer.country,
    phone: paypalCustomer.phone.length > 0 ? decodeURIComponent(paypalCustomer.phone) : customer.phone,
    // recently we have experience the issue where despite having legit paypal account, chargebee is not able to collect the payment
    // that led us to discover that the payer status can be verified/unverified, and if unverified it could return message like no valid card on file
    payerStatus: paypalCustomer.payerStatus
  };
  return gofarCustomer;
};

export const addRayToOrder = async (subscriptionId) => {
  await gofarService(`/api/checkout/chargebee/addchargetoorder`, HTTP_METHODS.POST, {
    subscription_id: subscriptionId
  });
};